import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { api } from "@/api/client";
import { DEFAULT_LOCALE } from "@/i18n";
import { PortalProvider, type PortalContextValue } from "@/components/portal/PortalContext";
import PortalLayout from "@/components/portal/PortalLayout";

type State =
  | { kind: "loading" }
  | { kind: "authed"; value: PortalContextValue }
  | { kind: "anon" };

/**
 * Gates portal routes on the httpOnly `ce_org` session AND provides the portal
 * context. Calls GET /api/org/auth/me (which returns the exact {user, org}
 * PortalContextValue shape) through the same-origin proxy: 200 -> render the
 * authed routes wrapped in PortalProvider so usePortal() works in settings/team;
 * any failure (401 or otherwise) -> /:locale/portal/login, since the pages can't
 * render without the context value. (Replaces the Next portal layout's provider.)
 */
export default function PortalGuard() {
  const { locale } = useParams();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let alive = true;
    api
      .get<PortalContextValue>("/api/org/auth/me")
      .then((me) => alive && setState({ kind: "authed", value: me }))
      .catch(() => alive && setState({ kind: "anon" }));
    return () => {
      alive = false;
    };
  }, []);

  if (state.kind === "loading") return <div className="p-8 text-txt-tertiary">Loading…</div>;
  if (state.kind === "anon")
    return <Navigate to={`/${locale ?? DEFAULT_LOCALE}/portal/login`} replace />;

  return (
    <PortalProvider value={state.value}>
      <PortalLayout />
    </PortalProvider>
  );
}
